import React, { useEffect, useState } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { ShoppingBag, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, formatTL } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ProductGallery from "@/components/ProductGallery";
import { useCart } from "@/contexts/CartContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const { settings } = useOutletContext() || {};
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then(async (r) => {
        setProduct(r.data);
        setQty(1);
        const rel = await api.get("/products", { params: { category: r.data.category_slug, limit: 8 } });
        setRelated(rel.data.filter((p) => p.id !== r.data.id).slice(0, 4));
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const currency = settings?.currency_symbol || "₺";

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-larc-muted">Yükleniyor…</div>;
  if (!product) return (
    <div className="max-w-7xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
      <h2 className="font-heading text-4xl">Ürün bulunamadı</h2>
      <Link to="/urunler" className="mt-6 inline-flex items-center gap-2 text-larc-gold"><ArrowLeft size={14} /> Tüm ürünler</Link>
    </div>
  );

  const onSale = product.sale_price && product.sale_price < product.price;
  const finalPrice = onSale ? product.sale_price : product.price;
  const inStock = product.stock > 0;

  const handleAdd = () => {
    if (!inStock) { toast.error("Bu ürün tükendi"); return; }
    addItem(product, qty);
    toast.success(`${product.name} sepete eklendi`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="product-detail">
      <Link to="/urunler" className="text-sm text-larc-muted hover:text-larc-ink inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Ürünlere dön</Link>

      <div className="mt-8 grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Gallery */}
        <div className="md:sticky md:top-28">
          <ProductGallery images={product.images || []} alt={product.name} />
        </div>

        {/* Info */}
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-larc-gold">{product.category_slug.replace("-", " ")}</div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight mt-3">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            {onSale && <span className="text-larc-muted line-through text-lg">{formatTL(product.price, currency)}</span>}
            <span className="text-2xl text-larc-ink font-medium">{formatTL(finalPrice, currency)}</span>
            {inStock ? (
              <span className="ml-2 text-xs uppercase tracking-[0.18em] text-green-700 bg-green-50 border border-green-100 rounded-full px-3 py-1">Stokta</span>
            ) : (
              <span className="ml-2 text-xs uppercase tracking-[0.18em] text-red-700 bg-red-50 border border-red-100 rounded-full px-3 py-1">Tükendi</span>
            )}
          </div>
          <p className="mt-6 text-larc-muted leading-relaxed">{product.short_description}</p>

          {/* Specs */}
          <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
            {product.size && <Spec label="Boyut / Hacim" value={product.size} />}
            {product.burning_time && <Spec label="Yanma Süresi" value={product.burning_time} />}
            {product.scent_notes && <Spec label="Koku Notaları" value={product.scent_notes} />}
            {product.skin_suitability && <Spec label="Cilt Uygunluğu" value={product.skin_suitability} />}
            {product.sku && <Spec label="SKU" value={product.sku} />}
          </div>

          {/* Quantity + add to cart */}
          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center bg-white border border-larc-border rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-12 w-12 text-larc-muted hover:text-larc-ink" data-testid="qty-minus">−</button>
              <span className="w-10 text-center font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="h-12 w-12 text-larc-muted hover:text-larc-ink" data-testid="qty-plus">+</button>
            </div>
            <button onClick={handleAdd} disabled={!inStock} className="flex-1 inline-flex items-center justify-center gap-2 bg-larc-gold text-white rounded-full px-8 py-3.5 text-sm hover:bg-larc-gold-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-95" data-testid="add-to-cart">
              <ShoppingBag size={16} /> Sepete Ekle
            </button>
          </div>

          {product.customizable && (
            <Link to="/kisiye-ozel" className="mt-4 block text-center text-sm text-larc-muted hover:text-larc-ink underline-offset-4 hover:underline">
              ✦ Bu ürünü kişiye özel sipariş edebilirsiniz
            </Link>
          )}

          {/* Description & ingredients */}
          {(product.description || product.ingredients) && (
            <div className="mt-12 border-t border-larc-border pt-8 space-y-6">
              {product.description && (
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-larc-gold mb-2">Açıklama</div>
                  <p className="text-larc-muted leading-relaxed">{product.description}</p>
                </div>
              )}
              {product.ingredients && (
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-larc-gold mb-2">İçindekiler</div>
                  <p className="text-larc-muted leading-relaxed">{product.ingredients}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-24 md:mt-32">
          <h2 className="font-heading text-3xl md:text-4xl">Birlikte sevilenler</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {related.map((p) => <ProductCard key={p.id} product={p} currency={currency} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.18em] text-larc-gold">{label}</div>
      <div className="text-larc-ink mt-1">{value}</div>
    </div>
  );
}
